/**
 * WorkOrderScheduleActions.tsx — Phase 2, qa/quote-wo-schedule-lifecycle-2026-07-11.
 *
 * All schedule-aware behavior of the work-order editor, ported from the
 * orphaned pages/workorders/WorkOrderDetail.tsx (unrouted since bd2b110,
 * deleted in this phase) onto the LIVE surface (WorkOrdersTabContent):
 *
 *   - ScheduleLinkBanner + useSourceScheduleImpact (FEAT-2026-0012): view /
 *     sync (incl. the W2-4 blocked-sync "Release & sync" confirm ceremony).
 *   - Add to Schedule → relabels to "Edit Schedule" once a plan is attached
 *     (same deep-link either way; the scenario resolve flow resumes the draft).
 *   - Commit Schedule button (Phase 3 item 10 + W2-6 scoping + G2-1 advisory
 *     pre-commit check).
 *   - Guarded delete (buildScheduleDeleteDialog cascade / release ceremony,
 *     P2-8/P2-9 server-message surfacing).
 *   - Guarded status change (useWorkOrderStatusChange, P2-4).
 *   - Converted-from-quote arrival toast (#314) — the convert flow navigates
 *     here with location.state.convertedFromQuote.
 *
 * Shape: a hook returning ready-to-render slots (banner, buttons) plus the
 * guarded handlers, so WorkOrdersTabContent stays under the 600-line cap.
 */

import { Button, CircularProgress, Tooltip } from '@mui/material';
import { CheckCircleOutline, EventAvailable } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ROUTES } from '../../constants/routes';
import type { ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { ScheduleLinkBanner } from '../../components/schedule/ScheduleLinkBanner';
import { buildSubmitWarnings } from '../../components/schedule/submitWarnings';
import { SubmitWarningsContent } from '../../components/schedule/SubmitWarningsContent';
import type { SubmitCheckResult } from '../../services/scheduleScenario';
import {
  useSourceScheduleImpact,
  isSyncBlocked,
  buildSyncSnackbar,
  buildSyncReleaseDialog,
  SYNC_FAILED_MESSAGE,
} from '../../hooks/useSourceScheduleImpact';
import { useWorkOrderStatusChange } from '../../hooks/useWorkOrderStatusChange';
import { usePermissions } from '../../hooks/usePermissions';
import {
  commitSchedule,
  fetchCommitStatus,
  fetchCommitCheck,
  type CommitStatusSummary,
} from '../../services/scheduleCommit';
import { useWorkOrderDeleteFlow } from '../../hooks/useWorkOrderDeleteFlow';
import { WoDeleteDialog } from './WoDeleteDialog';
import { buildWoDeleteNotes, requiresReleaseFromPreview } from './woDeleteNotes';
import type { SourceScheduleImpact } from '../../services/scheduleScenario';
import type { ScheduleReturnTo } from '../../types/schedule';
import type { WorkOrder } from '../../types/workOrder';

type Snackbar = { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' };

/**
 * Pure relabel rule for the schedule entry-point button: once the impact probe
 * says a plan is attached, "Add to Schedule" reads "Edit Schedule" (same
 * navigation — the scenario resolve flow resumes the existing draft/plan).
 * Exported for vitest.
 */
export function resolveScheduleEntryLabel(
  impact: Pick<SourceScheduleImpact, 'hasScenario'> | null,
): 'Add to Schedule' | 'Edit Schedule' {
  return impact?.hasScenario ? 'Edit Schedule' : 'Add to Schedule';
}

interface UseWorkOrderScheduleActionsArgs {
  workOrderId: string | undefined;
  workOrder: WorkOrder | null;
  /** Number of WO detail rows — gates the Commit button like the orphan did. */
  detailsCount: number;
  /** Completed/signed-off detail rows — drives the typed-delete confirmation. */
  completedCount: number;
  /** The project's human id, shown in the delete dialog. */
  projectId?: string | null;
  setWorkOrder: React.Dispatch<React.SetStateAction<WorkOrder | null>>;
  setSnackbar: (s: Snackbar) => void;
  setConfirmDlg: (s: ConfirmDialogState) => void;
  /** Called after a successful delete (navigate away). */
  onDeleted: () => void;
}

interface UseWorkOrderScheduleActionsResult {
  /** ScheduleLinkBanner slot — render above the header row. */
  banner: ReactNode;
  /** Add/Edit Schedule + Commit Schedule buttons — render in the action row. */
  scheduleButtons: ReactNode;
  /** P2-4 guarded status change (replaces bare updateWorkOrder status writes). */
  handleStatusChange: (statusKey: string, label: string) => Promise<void>;
  /** Opens the hardened delete confirmation (WoDeleteDialog). */
  handleDelete: () => void;
  /** The hardened delete dialog — render once alongside the grid. */
  deleteDialog: ReactNode;
  /** Refresh the banner impact (call after sample add/remove — G2-4 drift cue). */
  refetchImpact: () => Promise<unknown>;
}

export function useWorkOrderScheduleActions({
  workOrderId,
  workOrder,
  detailsCount,
  completedCount,
  projectId,
  setWorkOrder,
  setSnackbar,
  setConfirmDlg,
  onDeleted,
}: UseWorkOrderScheduleActionsArgs): UseWorkOrderScheduleActionsResult {
  const { canEditScheduling } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // ── #314: arriving from a quote conversion — report what carried over ─────
  // (one-shot; the state is cleared so a refresh doesn't re-toast).
  useEffect(() => {
    const converted = (location.state as {
      convertedFromQuote?: {
        quoteNumber: string; detailsCreated: number; tasksMigrated: number; unmappedLines: string[];
        placeholderTests?: string[];
      };
    } | null)?.convertedFromQuote;
    if (!converted) return;
    const unmappedNote = converted.unmappedLines.length > 0
      ? ` Not carried over (no lab-test mapping): ${converted.unmappedLines.join(', ')} — still on the quote.`
      : '';
    const placeholderNote = (converted.placeholderTests?.length ?? 0) > 0
      ? ` No digital module yet for: ${converted.placeholderTests!.join(', ')} — created as placeholder tests ("test coming"); nothing lost.`
      : '';
    setSnackbar({
      open: true,
      message: `Work order created from quote ${converted.quoteNumber} — ${converted.detailsCreated} test row(s), ${converted.tasksMigrated} schedule task(s) carried over.${placeholderNote}${unmappedNote}`,
      severity: converted.unmappedLines.length > 0 || (converted.placeholderTests?.length ?? 0) > 0 ? 'warning' : 'success',
    });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Commit Schedule state (Phase 3 item 10 / G2-1) ────────────────────────
  const [committingSchedule, setCommittingSchedule] = useState(false);
  const [checkingCommit, setCheckingCommit] = useState(false);
  const [commitStatus, setCommitStatus] = useState<CommitStatusSummary | null>(null);

  const refreshCommitStatus = useCallback(async () => {
    if (!workOrderId) return;
    try {
      const s = await fetchCommitStatus({ source: 'WorkOrder', sourceKey: workOrderId });
      setCommitStatus(s);
    } catch { /* non-fatal */ }
  }, [workOrderId]);
  useEffect(() => { refreshCommitStatus(); }, [refreshCommitStatus]);

  // ── FEAT-2026-0012: impact probe → banner + delete ceremony ───────────────
  const {
    impact: scheduleImpact,
    loading: impactLoading,
    syncing: impactSyncing,
    sync: syncSchedule,
    syncConfirmed: syncScheduleConfirmed,
    refetch: refetchImpact,
  } = useSourceScheduleImpact('WorkOrder', workOrderId);

  /**
   * Open Scenario Planning filtered to this work order (view OR add/edit).
   * Back-nav fix (2026-07-11): carries the origin as navigation state so
   * Scenario Planning can render an explicit "Back to {WO id}" button.
   */
  const handleViewSchedule = useCallback(() => {
    if (!workOrderId) return;
    const scheduleReturnTo: ScheduleReturnTo = {
      path: `${location.pathname}${location.search}`,
      label: workOrder?.WorkOrderId || 'Work Order',
    };
    navigate(`${ROUTES.SCHEDULE_SCENARIO}?source=WorkOrder&sourceKey=${encodeURIComponent(workOrderId)}`, {
      state: { scheduleReturnTo },
    });
  }, [workOrderId, navigate, location.pathname, location.search, workOrder?.WorkOrderId]);

  /**
   * Re-sync this work order's scenario with its current details. W2-4: when
   * the sync would release COMMITTED bookings the backend refuses (409) and
   * sync() resolves blocked — raise the shared ConfirmDialog and only retry
   * with confirmRelease on confirm.
   */
  const handleSyncSchedule = useCallback(async () => {
    try {
      const result = await syncSchedule();
      if (!result) return;
      if (isSyncBlocked(result)) {
        setConfirmDlg(buildSyncReleaseDialog(result, async () => {
          try {
            const confirmed = await syncScheduleConfirmed();
            if (confirmed) setSnackbar({ open: true, ...buildSyncSnackbar(confirmed) });
          } catch {
            // P2-11: pipeline is transactional per step + idempotent — re-running converges.
            setSnackbar({ open: true, message: SYNC_FAILED_MESSAGE, severity: 'error' });
          }
        }));
        return;
      }
      setSnackbar({ open: true, ...buildSyncSnackbar(result) });
    } catch {
      setSnackbar({ open: true, message: SYNC_FAILED_MESSAGE, severity: 'error' });
    }
  }, [syncSchedule, syncScheduleConfirmed, setConfirmDlg, setSnackbar]);

  // ── P2-4: guarded status change ────────────────────────────────────────────
  const handleStatusChange = useWorkOrderStatusChange({
    workOrderId,
    setWorkOrder,
    setSnackbar,
    setConfirmDlg,
    refetchImpact,
    refreshCommitStatus,
  });

  // ── Hardened delete (incident WO-2026-110, 2026-08-05) ────────────────────
  // The old flow showed a bare "Are you sure?" for schedule-less WOs — no
  // number, no test counts — and a real WO with 12 signed-off tests died to a
  // mistaken cleanup click. WoDeleteDialog names everything and requires
  // typing the WO number when completed tests exist. 2026-09-11: the state
  // machine (preview probe, DELETE, 409 release ceremony) is the shared
  // useWorkOrderDeleteFlow — the same one the /workorders row delete uses.
  const deleteFlow = useWorkOrderDeleteFlow({ onDeleted });
  const { openFor: openDeleteFor } = deleteFlow;

  const handleDelete = useCallback(() => {
    if (!workOrderId) return;
    openDeleteFor({
      workOrderKey: workOrderId,
      workOrderId: workOrder?.WorkOrderId || 'this work order',
      workOrderName: workOrder?.WorkOrderName ?? null,
      projectId: projectId ?? null,
    });
  }, [workOrderId, workOrder?.WorkOrderId, workOrder?.WorkOrderName, projectId, openDeleteFor]);

  const requireRelease = deleteFlow.forceRelease
    || Boolean(scheduleImpact?.hasFinalized)
    || (scheduleImpact?.sourceCommittedBookings ?? 0) > 0
    || requiresReleaseFromPreview(deleteFlow.preview);
  const scheduleNote = (() => {
    if (!scheduleImpact) return null;
    const committed = scheduleImpact.sourceCommittedBookings ?? 0;
    const plans = scheduleImpact.scenarios?.length ?? 0;
    if (committed === 0 && plans === 0) return null;
    const parts: string[] = [];
    if (plans > 0) parts.push(`${plans} schedule plan${plans === 1 ? '' : 's'}`);
    if (committed > 0) parts.push(`${committed} COMMITTED booking${committed === 1 ? '' : 's'}`);
    return `Schedule attached: ${parts.join(' + ')} — deleting releases the machine/people time.`;
  })();

  const deleteDialog: ReactNode = (
    <WoDeleteDialog
      open={deleteFlow.target !== null}
      workOrderId={workOrder?.WorkOrderId || 'this work order'}
      workOrderName={workOrder?.WorkOrderName ?? null}
      projectId={projectId ?? null}
      testCount={detailsCount}
      completedCount={Math.max(completedCount, deleteFlow.preview?.completedCount ?? 0)}
      scheduleNote={scheduleNote}
      notes={buildWoDeleteNotes(deleteFlow.preview, { includeSchedule: false })}
      requireRelease={requireRelease}
      busy={deleteFlow.busy}
      error={deleteFlow.error}
      onCancel={deleteFlow.close}
      onConfirm={(release) => { void deleteFlow.confirm(release); }}
    />
  );

  /**
   * Commit Schedule — scope per W2-6 (Option B): this WO's live-board
   * (BASELINE) Tentative rows plus, when it has exactly one draft plan, that
   * whole plan (which is then locked/Submitted). G2-1: the read-only
   * commit-check runs first; its advisory bullets render in the dialog but
   * never block the commit.
   */
  const handleCommitSchedule = useCallback(async () => {
    if (!workOrderId || !workOrder || checkingCommit) return;
    const drafts = commitStatus?.draftScenarios ?? [];
    const baseMessage =
      drafts.length === 1
        ? `Commits this work order's tentative bookings and assignments on the live board and in its own plan "${drafts[0].code} — ${drafts[0].name}". That whole plan is committed and locked (Submitted) — use "Reopen to Edit" on the plan to change it later. Other draft plans are not affected.`
        : drafts.length > 1
          ? `Commits this work order's tentative bookings and assignments on the live board only. This work order has ${drafts.length} draft plans (${drafts.map((d) => d.code).join(', ')}) — with more than one draft, none is auto-committed; submit the right one from the Schedule planning page. Draft plans are not affected.`
          : `Commits this work order's tentative bookings and assignments on the live board to the confirmed schedule. Draft plans are not affected.`;

    // Structured advisory sections in the dialog body (SubmitWarningsContent,
    // 2026-08-05); the plain-text builder still decides warn-vs-plain.
    let warnings: string | null = null;
    let warnCheck: SubmitCheckResult | null = null;
    setCheckingCommit(true);
    try {
      const check = await fetchCommitCheck({
        source: 'WorkOrder',
        sourceKey: String(workOrder.WorkOrderKey),
      });
      warnings = buildSubmitWarnings(check);
      if (warnings) warnCheck = check;
    } catch {
      warnings = null; // check unavailable — proceed with the standard confirm
    } finally {
      setCheckingCommit(false);
    }

    setConfirmDlg({
      open: true,
      title: warnings ? 'Commit Schedule anyway?' : 'Commit Schedule',
      message: baseMessage,
      body: warnCheck ? <SubmitWarningsContent check={warnCheck} /> : undefined,
      confirmLabel: warnings ? 'Commit anyway' : 'Commit',
      confirmColor: warnings ? 'warning' : 'primary',
      onConfirm: async () => {
        setCommittingSchedule(true);
        try {
          const result = await commitSchedule({
            source: 'WorkOrder',
            sourceKey: String(workOrder.WorkOrderKey),
          });
          const parts: string[] = [];
          if (result.bookingsCommitted) parts.push(`${result.bookingsCommitted} booking(s)`);
          if (result.assignmentsCommitted) parts.push(`${result.assignmentsCommitted} assignment(s)`);
          const detail = parts.length > 0 ? parts.join(', ') : 'no changes — already committed';
          const lockNote = result.scenarioSubmitted
            ? ` Plan "${result.scenarioSubmitted.code}" is now locked (Submitted).`
            : '';
          const skipNote = result.draftsSkipped?.length
            ? ` Draft plans not committed: ${result.draftsSkipped.join(', ')}.`
            : '';
          setSnackbar({
            open: true,
            message: `Schedule committed — ${detail} now Committed.${lockNote}${skipNote}`,
            severity: 'success',
          });
          await refreshCommitStatus();
        } catch {
          setSnackbar({ open: true, message: 'Failed to commit schedule', severity: 'error' });
        } finally {
          setCommittingSchedule(false);
        }
      },
    });
  }, [workOrderId, workOrder, commitStatus, refreshCommitStatus, checkingCommit, setConfirmDlg, setSnackbar]);

  // ── Render slots ──────────────────────────────────────────────────────────
  const banner = (
    <ScheduleLinkBanner
      impact={scheduleImpact}
      loading={impactLoading}
      syncing={impactSyncing}
      onView={handleViewSchedule}
      onSync={handleSyncSchedule}
    />
  );

  const entryLabel = resolveScheduleEntryLabel(scheduleImpact);
  const tentTotal = (commitStatus?.tentativeBookings ?? 0) + (commitStatus?.tentativeAssignments ?? 0);
  const commTotal = (commitStatus?.committedBookings ?? 0) + (commitStatus?.committedAssignments ?? 0);
  const nothingToCommit = commitStatus !== null && tentTotal === 0;
  const fullyCommitted = nothingToCommit && commTotal > 0;
  const commitLabel = fullyCommitted
    ? 'Schedule Committed'
    : nothingToCommit
      ? 'Nothing to Commit'
      : tentTotal > 0
        ? `Commit Schedule (${tentTotal})`
        : 'Commit Schedule';

  const scheduleButtons = canEditScheduling ? (
    <>
      {/* BUG-2026-0101 — Scenario Planning entry point. Relabels once a plan
          is attached; the resolve flow resumes the existing draft either way. */}
      <Tooltip title={
        entryLabel === 'Edit Schedule'
          ? 'Open this work order’s schedule plan in Scenario Planning.'
          : 'Open Scenario Planning to schedule machines and technicians for this work order.'
      }>
        <Button variant="outlined" startIcon={<EventAvailable />} onClick={handleViewSchedule}>
          {entryLabel}
        </Button>
      </Tooltip>
      <Tooltip title={
        fullyCommitted
          ? `All ${commTotal} booking(s)/assignment(s) are already Committed.`
          : nothingToCommit
            ? 'No tentative bookings or assignments exist for this work order yet.'
            : commitStatus?.draftScenarios?.length === 1
              ? `${tentTotal} tentative item(s) will be flipped to Committed — includes plan ${commitStatus.draftScenarios[0].code}, which will be locked (Submitted).`
              : `${tentTotal} tentative item(s) will be flipped to Committed.`
      }>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={committingSchedule || checkingCommit ? <CircularProgress size={14} /> : <CheckCircleOutline />}
            onClick={handleCommitSchedule}
            disabled={committingSchedule || checkingCommit || detailsCount === 0 || nothingToCommit}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            {commitLabel}
          </Button>
        </span>
      </Tooltip>
    </>
  ) : null;

  return { banner, scheduleButtons, handleStatusChange, handleDelete, deleteDialog, refetchImpact };
}
